/**
 * As peças do caso numa tabela navegável (E78.F3.T07).
 *
 * Substitui os N cards empilhados — no caso real, cinco blocos com o formulário
 * inteiro e o quadro completo dentro de cada um. Aqui as cinco cabem numa tela:
 * letra e arquivo, o código declarado no protocolo (editável, porque é o único
 * dado que muda de peça para peça), o hash confrontado com o protocolo, alertas
 * e o processamento. Clicar na linha abre o quadro daquela peça.
 *
 * E91.F2 (GUS-51 RF04): a coluna "Confronto" virou "Hash com o protocolo",
 * com um estado por documento e tons distintos; saíram o código apurado (o
 * hash completo mora no detalhe da peça) e o grau (mora no quadro), e
 * "Situação" passou a se chamar "Processamento" — porque "Verificado" ali
 * nunca foi aprovação, só o registro de que o motor rodou.
 *
 * O confronto e a contagem de alertas são LIDOS do quadro que o motor gravou —
 * a tabela não compara hash nenhum.
 */
import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type BadgeProps,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData, ExameEletronicoStatus } from '@/lib/exame_eletronico';
import { codigoAlterado } from '@/lib/exame_eletronico_edicao';
import { hashComOProtocolo } from '@/lib/exame_eletronico_hash';
import { alertasDaPeca, tituloDaPeca } from '@/lib/exame_eletronico_peca';

import { CelulaNomeDoDocumento } from './CelulaNomeDoDocumento';
import { classeEmAtualizacao } from './EmAtualizacao';
import { HashComOProtocoloBadge } from './HashComOProtocolo';

const t = ptBR.exameEletronico;
const tp = ptBR.exameEletronico.tabelaDePecas;

const STATUS_META: Record<
  ExameEletronicoStatus | 'sem_protocolo',
  { rotulo: string; variant: NonNullable<BadgeProps['variant']> }
> = {
  sem_protocolo: { rotulo: t.statusSemProtocolo, variant: 'neutral' },
  rascunho: { rotulo: t.statusRascunho, variant: 'info' },
  verificado: { rotulo: t.statusVerificado, variant: 'success' },
  concluido: { rotulo: t.statusConcluido, variant: 'success' },
};

export interface TabelaDePecasProps {
  pecas: readonly ExameEletronicoCcbData[];
  /**
   * E84.F9 (GUS-44 RF05/CA05) — a régua VIGENTE do motor.
   *
   * O contrato a devolve desde a E84.F5 e ninguém a consumia: um resultado
   * calculado por uma régua anterior — a que resolvia a data pelo metadado do
   * arquivo — se apresentava como se fosse da régua de hoje. A tabela compara e
   * avisa; nada é recalculado em silêncio.
   */
  parametrosVersaoCorrente?: string;
  /** `source_document_id` da peça cujo quadro está aberto. */
  selecionada: string | null;
  /** Código declarado em edição, por peça (o salvo, enquanto ninguém digita). */
  rascunhos: Readonly<Record<string, string>>;
  /** Peça com gravação em voo — trava a linha dela. */
  salvando: string | null;
  erroPorPeca: Readonly<Record<string, string>>;
  onSelecionar: (sourceDocumentId: string) => void;
  onEditarCodigo: (sourceDocumentId: string, codigo: string) => void;
  onSalvarPeca: (sourceDocumentId: string) => void;
  /** Joga fora o rascunho da peça e devolve a linha ao gravado (GUS-45 RF03). */
  onDescartarPeca: (sourceDocumentId: string) => void;
  /** E90.F6: grava o nome dado pelo perito ao documento; `null` apaga. */
  onRenomear: (sourceDocumentId: string, nome: string | null) => void;
  /** E90.F6: peça com renomeação em voo — trava o controle dela. */
  renomeando?: string | null;
  /**
   * E89.F2 (GUS-50 RF05): o caso está sendo verificado. As colunas do resultado
   * seguem à vista, esmaecidas e marcadas, menos nas peças concluídas — que a
   * verificação pula, e cujo resultado continua sendo o atual.
   */
  emAtualizacao?: boolean;
}

function CelulaCodigoDeclarado({
  peca,
  valor,
  alterado,
  salvando,
  erro,
  onEditar,
  onSalvar,
  onDescartar,
}: {
  peca: ExameEletronicoCcbData;
  valor: string;
  alterado: boolean;
  salvando: boolean;
  erro: string | undefined;
  onEditar: (codigo: string) => void;
  onSalvar: () => void;
  onDescartar: () => void;
}): JSX.Element {
  const concluido = peca.exame?.status === 'concluido';
  return (
    // Editar não é navegar: o clique dentro da célula não troca a peça aberta.
    <div
      className="space-y-sc-1"
      onClick={(evento) => evento.stopPropagation()}
      role="presentation"
    >
      <Input
        aria-label={`${tp.colunaDeclarado} — ${tituloDaPeca(peca, 0)}`}
        className="w-full min-w-[12rem] font-mono text-body-sm"
        value={valor}
        disabled={salvando || concluido}
        maxLength={128}
        onChange={(evento) => onEditar(evento.target.value)}
        data-testid={`peca-codigo-${peca.source_document_id}`}
      />
      {erro !== undefined ? (
        <p
          className="text-caption text-danger-700"
          role="alert"
          data-testid={`peca-erro-${peca.source_document_id}`}
        >
          {erro}
        </p>
      ) : null}
      {/* GUS-45 RN04: o Salvar existe sempre e fica DESABILITADO sem mudança
          real — some, o perito não sabe se a ação não existe ou se ele não
          alterou nada, e o leitor de tela perde o alvo entre um render e
          outro. O Descartar só aparece quando há o que descartar. */}
      {concluido ? null : (
        <div className="flex flex-wrap gap-sc-2">
          <Button
            type="button"
            variant="secondary"
            disabled={salvando || !alterado}
            onClick={onSalvar}
            data-testid={`peca-salvar-${peca.source_document_id}`}
          >
            {salvando ? t.form.salvando : tp.salvarCodigo}
          </Button>
          {alterado ? (
            <Button
              type="button"
              variant="ghost"
              disabled={salvando}
              onClick={onDescartar}
              data-testid={`peca-descartar-${peca.source_document_id}`}
            >
              {tp.descartarCodigo}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * A régua com que a peça foi verificada, quando ela não é a vigente (E84.F9).
 *
 * Compara o `parametros_versao` que o exame carimbou com o
 * `parametros_versao_corrente` que o servidor devolve. `null` quando não há o
 * que comparar — peça sem resultado, exame sem versão carimbada (registro
 * anterior ao campo) ou servidor que não mandou a vigente.
 */
export function reguaDaPeca(
  peca: ExameEletronicoCcbData,
  corrente: string | undefined,
): { versao: string; corrente: string } | null {
  const versao = peca.exame?.parametros_versao ?? null;
  if (corrente === undefined || versao === null) return null;
  if (peca.exame?.resultado == null) return null;
  return versao === corrente ? null : { versao, corrente };
}

/**
 * O processamento da peça e, quando for o caso, a ressalva de que o resultado
 * exibido é anterior à alteração em tela (E84.F7 / GUS-45 RF02).
 */
function CelulaSituacao({
  docId,
  titulo,
  status,
  desatualizado,
  reguaAnterior,
}: {
  docId: string;
  titulo: string;
  status: ExameEletronicoStatus | 'sem_protocolo';
  desatualizado: boolean;
  reguaAnterior: { versao: string; corrente: string } | null;
}): JSX.Element {
  return (
    <div className="space-y-sc-1">
      <Badge
        variant={STATUS_META[status].variant}
        data-testid={`peca-situacao-${docId}`}
        data-status={status}
      >
        {STATUS_META[status].rotulo}
      </Badge>
      {desatualizado ? (
        <Badge
          variant="warning"
          data-testid={`peca-desatualizada-${docId}`}
          aria-label={tp.resultadoDesatualizadoAria.replace('{peca}', titulo)}
        >
          {tp.resultadoDesatualizado}
        </Badge>
      ) : null}
      {reguaAnterior !== null ? (
        <Badge
          variant="warning"
          data-testid={`peca-regua-anterior-${docId}`}
          data-versao={reguaAnterior.versao}
          aria-label={tp.reguaAnteriorAria
            .replace('{peca}', titulo)
            .replace('{versao}', reguaAnterior.versao)
            .replace('{corrente}', reguaAnterior.corrente)}
        >
          {tp.reguaAnterior}
        </Badge>
      ) : null}
    </div>
  );
}

/**
 * A linha recebe a marca de atualização (E89.F2, RF05)? Peça concluída não:
 * a verificação a pula, e o resultado dela continua sendo o atual.
 */
function linhaEmAtualizacao(
  emAtualizacao: boolean,
  status: ExameEletronicoStatus | 'sem_protocolo',
): boolean {
  return emAtualizacao && status !== 'concluido';
}

export function TabelaDePecas({
  pecas,
  parametrosVersaoCorrente,
  selecionada,
  rascunhos,
  salvando,
  erroPorPeca,
  onSelecionar,
  onEditarCodigo,
  onSalvarPeca,
  onDescartarPeca,
  onRenomear,
  renomeando = null,
  emAtualizacao = false,
}: TabelaDePecasProps): JSX.Element {
  return (
    <div className="overflow-x-auto" data-testid="tabela-de-pecas" aria-busy={emAtualizacao}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{tp.colunaPeca}</TableHead>
            <TableHead>{tp.colunaDeclarado}</TableHead>
            <TableHead>{tp.colunaHash}</TableHead>
            <TableHead>{tp.colunaAlertas}</TableHead>
            <TableHead>{tp.colunaSituacao}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pecas.map((peca, indice) => {
            const docId = peca.source_document_id;
            const titulo = tituloDaPeca(peca, indice);
            const status = peca.exame?.status ?? 'sem_protocolo';
            const alertas = alertasDaPeca(peca);
            // O MESMO predicado do servidor (E84.F7.T01): digitar o gravado de
            // novo, com outra caixa ou com espaços, não é alteração.
            const alterado = codigoAlterado(peca, rascunhos[docId] ?? '');
            // GUS-45 RF02: só há "resultado anterior" a declarar se existe um
            // resultado na linha. Peça nunca verificada não tem o que ficar
            // desatualizado.
            const resultadoDesatualizado = alterado && peca.exame?.resultado != null;
            // E84.F9 (CA05): só há régua anterior a declarar quando existe
            // resultado E a versão carimbada difere da vigente. Peça nunca
            // verificada não tem régua a comparar.
            const reguaAnterior = reguaDaPeca(peca, parametrosVersaoCorrente);
            const emAtualizacaoNaLinha = linhaEmAtualizacao(emAtualizacao, status);
            const resultado = classeEmAtualizacao(emAtualizacaoNaLinha);
            return (
              <TableRow
                key={docId}
                className="cursor-pointer"
                data-testid="peca-linha"
                data-source-document={docId}
                data-selecionada={selecionada === docId}
                data-desatualizada={resultadoDesatualizado}
                data-em-atualizacao={emAtualizacaoNaLinha}
                onClick={() => onSelecionar(docId)}
              >
                <TableCell>
                  <CelulaNomeDoDocumento
                    peca={peca}
                    titulo={titulo}
                    salvando={renomeando === docId}
                    onSelecionar={() => onSelecionar(docId)}
                    onRenomear={(nome) => onRenomear(docId, nome)}
                  />
                </TableCell>
                <TableCell>
                  <CelulaCodigoDeclarado
                    peca={peca}
                    valor={rascunhos[docId] ?? ''}
                    alterado={alterado}
                    salvando={salvando === docId}
                    erro={erroPorPeca[docId]}
                    onEditar={(codigo) => onEditarCodigo(docId, codigo)}
                    onSalvar={() => onSalvarPeca(docId)}
                    onDescartar={() => onDescartarPeca(docId)}
                  />
                </TableCell>
                <TableCell className={`whitespace-nowrap ${resultado}`}>
                  <HashComOProtocoloBadge
                    estado={hashComOProtocolo(peca, emAtualizacao)}
                    titulo={titulo}
                    testId={`peca-hash-${docId}`}
                  />
                </TableCell>
                <TableCell className={resultado} data-testid={`peca-alertas-${docId}`}>
                  {alertas ?? '—'}
                </TableCell>
                <TableCell className={resultado}>
                  <CelulaSituacao
                    docId={docId}
                    titulo={titulo}
                    status={status}
                    desatualizado={resultadoDesatualizado}
                    reguaAnterior={reguaAnterior}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
